import {
    Catalog,
    CatalogVariation,
    getCatalog,
    getCatalogVariations,
    getCatalogsByCompany,
    catalogBasePrice,
} from '@/lib/api/catalog/catalog-api'
import { SearchableSelect } from '@/components/SearchableSelect'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import { HospitalSearchSelector } from '@/components/HospitalSearchSelector'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import { useEntityAccess } from '@/lib/hooks/use-entity-access'
import { mask } from '@/lib/form-state/util'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import {
    MdAdd,
    MdDelete,
    MdEdit,
    MdMedicalServices,
    MdScience,
} from 'react-icons/md'
import {
    OrderExamItem,
    OrderExamPartnerOption,
    OrderExamPriceSource,
    OrderExamProfessionalOption,
} from '../types'

export type LinkedCatalogKind = 'EXAM' | 'CONSULTATION'

interface OrderExamsFieldsProps {
    exams: OrderExamItem[]
    onExamsChange: (exams: OrderExamItem[]) => void
    companyId: number
    templateExams?: OrderExamItem[]
    kind?: LinkedCatalogKind
    /**
     * Hospital já escolhido em qualquer ponto do atendimento. Serve de padrão ao marcar
     * o repasse como HOSPITAL — o atendimento acontece em um hospital só, e obrigar a
     * buscá-lo de novo a cada linha é o caminho para o repasse sair para a casa errada.
     */
    defaultHospitalId?: number | null
    /**
     * Edição de VENDA DE PACOTE: mostra a agenda de cada componente.
     *
     * No agendamento comum a data é do atendimento inteiro e a coluna só ocuparia
     * espaço; na venda de pacote cada parte acontece num dia — a cirurgia numa data, a
     * consulta de retorno em outra — e sem a coluna esse dado fica gravado e invisível.
     */
    showComponentSchedule?: boolean
    /**
     * Salas da empresa, para escolher a de cada componente. Vazio esconde o campo —
     * empresa sem sala cadastrada não precisa da linha ocupando a coluna.
     */
    rooms?: Array<{ id: number; name: string }>
}

/**
 * Textos das seções de itens atrelados. O nome do registro-pai varia por
 * segmento (Procedimento na saúde, Pedido no resto), então a cópia é montada a
 * partir do rótulo em vez de ficar fixa.
 */
function buildKindCopy(orderLabel: string): Record<
    LinkedCatalogKind,
    {
        sectionTitle: string
        columnHeader: string
        searchPlaceholder: string
        searchPlaceholderLocked: (partner: string) => string
        addEmptyHint: string
        notFound: string
        notFoundLocked: string
        lockedAlert: string
    }
> {
    const lowerOrder = orderLabel.toLowerCase()
    return {
        EXAM: {
            sectionTitle: `Exames do ${orderLabel}`,
            columnHeader: 'Exame',
            searchPlaceholder: 'Buscar exame para adicionar...',
            searchPlaceholderLocked: p => `Buscar exame do parceiro ${p}...`,
            addEmptyHint: `Nenhum exame adicionado. Busque acima para atrelar exames ao ${lowerOrder}.`,
            notFound: 'Nenhum exame encontrado.',
            notFoundLocked: 'Nenhum exame deste parceiro encontrado.',
            lockedAlert:
                'selecionado em um dos exames. Somente exames disponíveis para este parceiro podem ser adicionados.',
        },
        CONSULTATION: {
            sectionTitle: `Consultas do ${orderLabel}`,
            columnHeader: 'Consulta',
            searchPlaceholder: 'Buscar consulta para adicionar...',
            searchPlaceholderLocked: p => `Buscar consulta do parceiro ${p}...`,
            addEmptyHint: `Nenhuma consulta adicionada. Busque acima para atrelar consultas ao ${lowerOrder}.`,
            notFound: 'Nenhuma consulta encontrada.',
            notFoundLocked: 'Nenhuma consulta deste parceiro encontrada.',
            lockedAlert:
                'selecionado em uma das consultas. Somente consultas disponíveis para este parceiro podem ser adicionadas.',
        },
    }
}

const formatCents = (cents: number): string =>
    `R$ ${(cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

// Aceita item pai (Catalog) OU variação (Catalog filho) — ambos têm partners/
// professionals no mesmo formato. Quando uma variação é escolhida, as opções de
// "Fonte do valor" passam a vir dela, reusando toda a lógica de preço da linha.
type PricingSource = { partners?: any[]; professionals?: any[] }

const extractPartners = (source: PricingSource): OrderExamPartnerOption[] =>
    (source.partners || [])
        .filter((p: any) => p.active !== false && p.partner)
        .map((p: any) => ({
            partnerId: p.partnerId,
            name: p.partner.name || p.partner.tradingName,
            price: p.sellingPrice || p.price || 0,
        }))

const extractProfessionals = (
    source: PricingSource
): OrderExamProfessionalOption[] =>
    (source.professionals || [])
        .filter((p: any) => p.active !== false && p.professional)
        .map((p: any) => ({
            professionalId: p.professionalId,
            name: p.professional.name,
            price: p.sellingPrice || p.price || 0,
        }))

// Preço-base da variação para a empresa (sellingPrice ?? price). O item pai usa
// catalogBasePrice; a variação tem companyPrices em formato próprio.
const variationBasePrice = (
    variation: CatalogVariation,
    companyId: number
): number => {
    const cp = (variation.companyPrices || []).find(
        p => p.companyId === companyId
    )
    return cp?.sellingPrice ?? cp?.price ?? 0
}

/**
 * Menor preço que a variação tem em ALGUM vínculo — só para dizer de onde o valor vai
 * vir quando o executante ainda não foi escolhido.
 *
 * A variação quase nunca tem preço próprio da empresa: quem tem preço é o vínculo com o
 * parceiro (ver `docs/catalog-variation-as-item-redesign.md`). Mostrar esse mínimo **sem
 * dizer que é um mínimo** é pior que mostrar zero: o operador lê um valor exato que não
 * corresponde a parceiro nenhum e não sabe de onde saiu. Daí o rótulo "a partir de", e
 * `null` quando nem os vínculos têm preço — aí é cadastro faltando.
 *
 * Mesma regra dos outros dois seletores de variação (`scheduling-form-page.tsx`).
 */
const variationPriceFrom = (variation: CatalogVariation): number | null => {
    const values = [
        ...extractPartners(variation).map(p => p.price),
        ...extractProfessionals(variation).map(p => p.price),
    ].filter(v => v > 0)
    return values.length ? Math.min(...values) : null
}

const priceFor = (exam: OrderExamItem): number => {
    // Valor personalizado: mantém o que o usuário digitou (não deriva de nada).
    if (exam.priceSource === 'custom') return exam.unitPrice
    if (exam.priceSource === 'partner' && exam.selectedPartnerId != null) {
        const opt = exam.availablePartners.find(
            p => p.partnerId === exam.selectedPartnerId
        )
        if (opt && opt.price > 0) return opt.price
    }
    if (
        exam.priceSource === 'professional' &&
        exam.selectedProfessionalId != null
    ) {
        const opt = exam.availableProfessionals.find(
            p => p.professionalId === exam.selectedProfessionalId
        )
        if (opt && opt.price > 0) return opt.price
    }
    return exam.itemPrice
}

/**
 * Valor do repasse quando ninguém digitou nada.
 *
 * O caso normal destes componentes é o VALOR CHEIO ir para quem executa: a consulta
 * pré-anestésica é do hospital, o exame é do laboratório, e a clínica não fica com
 * margem. A exceção é o parceiro com preço de custo cadastrado no vínculo — aí o custo
 * dele é menor que o cobrado, e é o custo que se paga.
 */
const defaultRecipientAmount = (exam: OrderExamItem): number => {
    if (exam.recipientKind === 'PARTNER' && exam.recipientId != null) {
        const link = exam.availablePartners.find(
            p => p.partnerId === exam.recipientId
        )
        if (link && link.price > 0) return link.price
    }
    // Cortesia zera o PREÇO, não o repasse: quem executou recebe do mesmo jeito, e quem
    // abriu mão da margem foi a clínica. Por isso cai no preço do item.
    return exam.unitPrice || exam.itemPrice
}

export default function OrderExamsFields({
    exams,
    onExamsChange,
    companyId,
    templateExams,
    kind = 'EXAM',
    defaultHospitalId = null,
    showComponentSchedule = false,
    rooms = [],
}: OrderExamsFieldsProps) {
    const { canSeeProfessionals } = useEntityAccess()
    const serviceLabels = useServiceLabel()
    const labels = buildKindCopy(serviceLabels.Order)[kind]
    const [search, setSearch] = useState('')
    const [results, setResults] = useState<Catalog[]>([])
    const [showDropdown, setShowDropdown] = useState(false)
    const [searching, setSearching] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const [initialized, setInitialized] = useState(false)
    // Linhas com o repasse aberto para edição. Fechado, ele é só o valor em texto — é o
    // caso normal, e um campo de digitação em toda linha convida a errar o que já está
    // certo.
    const [editingAmount, setEditingAmount] = useState<number[]>([])

    // Variações por item (Catalog filho). Carregadas sob demanda ao adicionar/hidratar.
    const [variationsByCatalog, setVariationsByCatalog] = useState<
        Record<number, CatalogVariation[]>
    >({})
    // Item pai carregado, para restaurar preço/parceiros ao LIMPAR a variação.
    const parentCatalogRef = useRef<Record<number, Catalog>>({})

    const loadVariationsFor = async (catalogId: number) => {
        if (catalogId in variationsByCatalog) return
        const res = await getCatalogVariations(catalogId, { scheduling: true })
        const active = (res.success ? res.result || [] : []).filter(
            v => v.active
        )
        setVariationsByCatalog(prev => ({ ...prev, [catalogId]: active }))
    }

    // Carrega variações de todos os itens presentes (cobre hidratação/template).
    useEffect(() => {
        exams.forEach(e => {
            if (!(e.catalogId in variationsByCatalog)) {
                void loadVariationsFor(e.catalogId)
            }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exams])

    /**
     * Trava de parceiro: escolhido um parceiro num exame, os demais exames têm de ser
     * dele. Vale só para EXAMES — cada consulta pode ter o seu.
     *
     * A regra continua; o que mudou foi de onde ela lê. Antes vinha da "Fonte do valor"
     * (`priceSource === 'partner'`), que deixou de existir; agora vem de QUEM RECEBE.
     *
     * ⚠️ O destinatário é livre e o item, não: escolher um parceiro que não tem aquele
     * exame no catálogo trava a busca em uma lista vazia. É o comportamento pedido —
     * quando acontecer, o caminho é limpar o destinatário ou cadastrar o item no
     * parceiro.
     */
    const lockedPartnerId = useMemo(() => {
        if (kind === 'CONSULTATION') return null
        const comParceiro = exams.find(
            e => e.recipientKind === 'PARTNER' && e.recipientId != null
        )
        return comParceiro?.recipientId ?? null
    }, [exams, kind])

    const lockedPartnerName = useMemo(() => {
        if (lockedPartnerId == null) return null
        // O nome vem do que foi escolhido na linha: o parceiro pode não estar em
        // `availablePartners` nenhum, já que a escolha não é mais restrita ao item.
        const naLinha = exams.find(
            e => e.recipientId === lockedPartnerId && e.recipientName
        )
        if (naLinha?.recipientName) return naLinha.recipientName
        for (const e of exams) {
            const found = e.availablePartners.find(
                p => p.partnerId === lockedPartnerId
            )
            if (found) return found.name
        }
        return null
    }, [lockedPartnerId, exams])

    useEffect(() => {
        if (
            !initialized &&
            templateExams &&
            templateExams.length > 0 &&
            exams.length === 0
        ) {
            onExamsChange(templateExams)
            setInitialized(true)
        }
    }, [templateExams])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setShowDropdown(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const searchExams = async (term: string) => {
        setSearch(term)
        if (term.length < 2) {
            setResults([])
            return
        }
        setSearching(true)
        try {
            const response = await getCatalogsByCompany(companyId, {
                category: kind,
                search: term,
                activeOnly: true,
            })
            if (response.success) {
                const existingIds = new Set(exams.map(e => e.catalogId))
                let list = (response.result || []).filter(
                    c => !existingIds.has(c.id)
                )
                if (lockedPartnerId != null) {
                    list = list.filter(c =>
                        (c.partners || []).some(
                            (p: any) =>
                                p.partnerId === lockedPartnerId &&
                                p.active !== false
                        )
                    )
                }
                setResults(list)
            }
        } catch {
            // ignore
        } finally {
            setSearching(false)
        }
    }

    const handleAdd = (catalog: Catalog) => {
        parentCatalogRef.current[catalog.id] = catalog
        void loadVariationsFor(catalog.id)
        const itemPrice = catalogBasePrice(catalog, companyId)
        const availablePartners = extractPartners(catalog)
        const availableProfessionals = extractProfessionals(catalog)
        // Exames e consultas atrelados ao procedimento começam com valor
        // "Personalizado" por padrão (não seguem parceiro/catálogo — o operador
        // digita o valor). Pode trocar a fonte manualmente depois.
        const priceSource: OrderExamPriceSource = 'custom'
        const selectedPartnerId: number | undefined = undefined
        const unitPrice = itemPrice
        onExamsChange([
            ...exams,
            {
                catalogId: catalog.id,
                name: catalog.name,
                catalogTypeName: catalog.catalogType?.name || '',
                itemPrice,
                unitPrice,
                quantity: 1,
                complimentary: false,
                priceSource,
                selectedPartnerId,
                availablePartners,
                availableProfessionals,
            },
        ])
        setSearch('')
        setResults([])
        setShowDropdown(false)
    }

    const handleRemove = (index: number) => {
        onExamsChange(exams.filter((_, i) => i !== index))
    }

    const handleQuantityChange = (index: number, quantity: number) => {
        onExamsChange(
            exams.map((e, i) =>
                i === index ? { ...e, quantity: Math.max(1, quantity) } : e
            )
        )
    }

    const handleToggleComplimentary = (index: number) => {
        onExamsChange(
            exams.map((e, i) =>
                i === index ? { ...e, complimentary: !e.complimentary } : e
            )
        )
    }

    /**
     * Escolher/limpar a variação. A variação é um Catalog filho com preço, parceiros e
     * profissionais PRÓPRIOS — ao escolher, trocamos itemPrice/availablePartners/
     * availableProfessionals pelos dela, então a coluna "Fonte do valor" passa a ofertar
     * os parceiros/profissionais da variação e todo o cálculo existente continua valendo.
     * A fonte volta para "item" (preço-base da variação); o operador reescolhe o parceiro.
     */
    // Aplica uma variação (ou limpa, variationId=null) a uma linha, trocando
    // preço/parceiros/profissionais pelos da variação (Catalog filho). Compartilhado
    // entre trocar a variação da linha e clonar uma linha com outra variação.
    const applyVariationToRow = (
        base: OrderExamItem,
        variationId: number | null
    ): OrderExamItem => {
        const variations = variationsByCatalog[base.catalogId] || []
        const source: PricingSource | undefined =
            variationId != null
                ? variations.find(v => v.id === variationId)
                : parentCatalogRef.current[base.catalogId]

        const basePrice =
            variationId != null && source
                ? variationBasePrice(source as CatalogVariation, companyId)
                : parentCatalogRef.current[base.catalogId]
                  ? catalogBasePrice(
                        parentCatalogRef.current[base.catalogId],
                        companyId
                    )
                  : base.itemPrice

        const availablePartners = source ? extractPartners(source) : []
        const availableProfessionals = source
            ? extractProfessionals(source)
            : []
        const chosen =
            variationId != null
                ? variations.find(v => v.id === variationId)
                : undefined

        return {
            ...base,
            catalogVariationId: variationId ?? undefined,
            variationName: chosen?.name,
            itemPrice: basePrice,
            unitPrice: basePrice,
            priceSource: 'item',
            selectedPartnerId: undefined,
            selectedProfessionalId: undefined,
            availablePartners,
            availableProfessionals,
        }
    }

    const handleVariationChange = (
        index: number,
        variationId: number | null
    ) => {
        const item = exams[index]
        const variations = variationsByCatalog[item.catalogId] || []
        if (
            variationId != null &&
            !variations.find(v => v.id === variationId)
        ) {
            return
        }
        onExamsChange(
            exams.map((e, i) =>
                i === index ? applyVariationToRow(e, variationId) : e
            )
        )
    }

    // Múltiplas variações do MESMO catálogo atrelado: cada variação vira uma linha
    // própria (mesmo catalogId, catalogVariationId distinto). No submit isso gera N
    // linhas de OrderLinkedCatalog (o schema é modelado para replicar linhas).
    const handleAddVariationRow = (index: number) => {
        const item = exams[index]
        const variations = variationsByCatalog[item.catalogId] || []
        const used = new Set(
            exams
                .filter(
                    e =>
                        e.catalogId === item.catalogId &&
                        e.catalogVariationId != null
                )
                .map(e => e.catalogVariationId)
        )
        const next = variations.find(v => !used.has(v.id))
        if (!next) return
        const copy = [...exams]
        copy.splice(index + 1, 0, applyVariationToRow(item, next.id))
        onExamsChange(copy)
    }

    // Hidratacao lazy: para exames sem availablePartners/Professionals carregados, buscar catalogo
    const hydratedRef = useRef<Set<number>>(new Set())
    useEffect(() => {
        const toFetch = exams.filter(
            e =>
                // Linha com variação escolhida tem parceiros/profissionais próprios
                // (podem ser vazios de propósito) — não re-hidratar do item pai.
                e.catalogVariationId == null &&
                e.availablePartners.length === 0 &&
                e.availableProfessionals.length === 0 &&
                !hydratedRef.current.has(e.catalogId)
        )
        if (toFetch.length === 0) return
        toFetch.forEach(e => hydratedRef.current.add(e.catalogId))
        Promise.all(toFetch.map(e => getCatalog(e.catalogId))).then(
            responses => {
                const byId: Record<number, Catalog> = {}
                responses.forEach(r => {
                    if (r.success && r.result) {
                        byId[r.result.id] = r.result
                        // Cacheia o pai para poder restaurar ao limpar a variação.
                        parentCatalogRef.current[r.result.id] = r.result
                    }
                })
                if (Object.keys(byId).length === 0) return
                onExamsChange(
                    exams.map(e => {
                        const cat = byId[e.catalogId]
                        if (!cat) return e
                        const availablePartners = extractPartners(cat)
                        const availableProfessionals = extractProfessionals(cat)
                        if (
                            availablePartners.length === 0 &&
                            availableProfessionals.length === 0
                        )
                            return e
                        return {
                            ...e,
                            availablePartners,
                            availableProfessionals,
                        }
                    })
                )
            }
        )
    }, [exams])

    // Manter unitPrice sincronizado caso availablePartners mude (ex: carga assincrona)
    useEffect(() => {
        let dirty = false
        const next = exams.map(e => {
            const expected = priceFor(e)
            if (expected !== e.unitPrice) {
                dirty = true
                return { ...e, unitPrice: expected }
            }
            return e
        })
        if (dirty) onExamsChange(next)
    }, [exams])

    // Hospital escolhido DEPOIS: preenche as linhas de hospital que ficaram vazias. Sem
    // isto, marcar o repasse como hospital antes de informar o hospital do atendimento
    // deixaria a linha em branco e o salvamento bloqueado, com o dado já disponível na
    // tela ao lado.
    useEffect(() => {
        if (!defaultHospitalId) return
        let dirty = false
        const next = exams.map(e => {
            if (e.recipientKind !== 'HOSPITAL' || e.recipientId) return e
            dirty = true
            return { ...e, recipientId: defaultHospitalId }
        })
        if (dirty) onExamsChange(next)
    }, [defaultHospitalId, exams])

    // O repasse acompanha o preço enquanto ninguém o editar. Sem isto, mudar o preço
    // deixaria para trás um repasse do valor antigo — silenciosamente errado, que é a
    // classe de erro que este trabalho todo está fechando.
    useEffect(() => {
        let dirty = false
        const next = exams.map(e => {
            if (e.recipientAmountEdited || !e.recipientKind) return e
            const expected = defaultRecipientAmount(e)
            if (expected !== e.recipientAmount) {
                dirty = true
                return { ...e, recipientAmount: expected }
            }
            return e
        })
        if (dirty) onExamsChange(next)
    }, [exams])

    // Valor unitário digitado à mão. Guarda em centavos e marca a linha como
    // personalizada — é o que impede uma troca posterior de destinatário de sobrescrever
    // o valor negociado.
    const handleUnitPriceChange = (index: number, cents: number) => {
        onExamsChange(
            exams.map((e, i) =>
                i === index
                    ? { ...e, unitPrice: cents, priceSource: 'custom' as const }
                    : e
            )
        )
    }

    /**
     * Destinatário do repasse deste componente.
     *
     * Trocar o TIPO zera o destinatário e o valor: um id de parceiro não é um id de
     * hospital, e manter o anterior pagaria a entidade errada — foi o cuidado que já
     * valeu no componente do pacote e no material.
     */
    const handleRecipientKindChange = (index: number, raw: string) => {
        const kind = (raw || null) as OrderExamItem['recipientKind']
        // Hospital já escolhido no atendimento entra preenchido: é o mesmo hospital.
        const herdado = kind === 'HOSPITAL' ? defaultHospitalId : null
        onExamsChange(
            exams.map((e, i) =>
                i === index
                    ? {
                          ...e,
                          recipientKind: kind,
                          recipientId: herdado,
                          recipientName: null,
                          recipientAmount: null,
                      }
                    : e
            )
        )
    }

    /**
     * Destinatário escolhido.
     *
     * Quando ele TEM vínculo com este item, o preço cobrado passa a ser o do vínculo —
     * é o que a antiga "Fonte do valor" fazia, agora derivado de quem executa em vez de
     * ser uma segunda escolha que podia contradizer a primeira. Sem vínculo (destinatário
     * livre, que é o caso do hospital), o preço do item continua valendo e segue
     * editável na coluna ao lado.
     */
    const handleRecipientEntityChange = (
        index: number,
        id: number | null,
        name: string | null
    ) => {
        onExamsChange(
            exams.map((e, i) => {
                if (i !== index) return e
                const next = { ...e, recipientId: id, recipientName: name }
                if (e.priceSource === 'custom' || id == null) return next

                const vinculo =
                    e.recipientKind === 'PARTNER'
                        ? e.availablePartners.find(p => p.partnerId === id)
                        : e.recipientKind === 'PROFESSIONAL'
                          ? e.availableProfessionals.find(
                                p => p.professionalId === id
                            )
                          : undefined

                // Grava também o `priceSource`/`selected*Id` correspondentes. Não é
                // resíduo do controle removido: um efeito ressincroniza `unitPrice` com
                // `priceFor(e)` sempre que os vínculos recarregam, e sem isso ele
                // devolveria o preço do item por cima do preço do vínculo.
                if (!vinculo || vinculo.price <= 0) {
                    return {
                        ...next,
                        priceSource: 'item' as const,
                        selectedPartnerId: undefined,
                        selectedProfessionalId: undefined,
                        unitPrice: e.itemPrice,
                    }
                }
                return e.recipientKind === 'PARTNER'
                    ? {
                          ...next,
                          priceSource: 'partner' as const,
                          selectedPartnerId: id,
                          selectedProfessionalId: undefined,
                          unitPrice: vinculo.price,
                      }
                    : {
                          ...next,
                          priceSource: 'professional' as const,
                          selectedPartnerId: undefined,
                          selectedProfessionalId: id,
                          unitPrice: vinculo.price,
                      }
            })
        )
    }

    /**
     * Data do componente. Guarda em ISO porque é o que o backend espera; o campo
     * `datetime-local` não tem fuso, então a conversão é local — igual à remarcação.
     */
    const handleScheduleChange = (index: number, raw: string) => {
        const iso = raw ? new Date(raw).toISOString() : null
        onExamsChange(
            exams.map((e, i) => (i === index ? { ...e, scheduledAt: iso } : e))
        )
    }

    const handleArrivalOrderChange = (index: number, checked: boolean) => {
        onExamsChange(
            exams.map((e, i) =>
                i === index ? { ...e, arrivalOrder: checked } : e
            )
        )
    }

    const handleRoomChange = (index: number, roomId: number | null) => {
        onExamsChange(
            exams.map((e, i) => (i === index ? { ...e, roomId } : e))
        )
    }

    const handleRecipientAmountChange = (index: number, raw: string) => {
        const cents = parseCurrencyDigits(raw)
        onExamsChange(
            exams.map((e, i) =>
                i === index
                    ? {
                          ...e,
                          recipientAmount: cents || null,
                          recipientAmountEdited: true,
                      }
                    : e
            )
        )
    }

    /** Volta o repasse a acompanhar o preço. */
    const handleRecipientAmountReset = (index: number) => {
        onExamsChange(
            exams.map((e, i) =>
                i === index
                    ? {
                          ...e,
                          recipientAmountEdited: false,
                          recipientAmount: defaultRecipientAmount(e),
                      }
                    : e
            )
        )
        setEditingAmount(prev => prev.filter(i => i !== index))
    }

    // Máscara de moeda: dígitos são interpretados como centavos (padrão do app).
    const currencyDisplay = (cents: number): string =>
        cents > 0
            ? mask(String(Math.round(cents)), '', 'currency', {
                  prefix: 'R$ ',
                  decimalSeparator: ',',
                  groupSeparator: '.',
                  precision: 2,
              })
            : ''
    const parseCurrencyDigits = (raw: string): number => {
        const digits = raw.replace(/\D/g, '')
        return digits ? parseInt(digits, 10) : 0
    }

    const total = useMemo(
        () =>
            exams.reduce(
                (sum, e) =>
                    sum + (e.complimentary ? 0 : e.unitPrice * e.quantity),
                0
            ),
        [exams]
    )

    if (!companyId) return null

    return (
        <div className="card bg-base-100 border border-base-300/50 shadow-sm">
            <div className="card-body">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="card-title text-lg flex items-center gap-2">
                        {kind === 'CONSULTATION' ? (
                            <MdMedicalServices className="text-success" />
                        ) : (
                            <MdScience className="text-info" />
                        )}
                        {labels.sectionTitle}
                    </h2>
                    {exams.length > 0 && (
                        <span className="text-sm font-semibold text-secondary">
                            Total: {formatCents(total)}
                        </span>
                    )}
                </div>

                {lockedPartnerId != null && lockedPartnerName && (
                    <div className="alert alert-warning py-2 px-3 text-xs mb-3">
                        <span>
                            Parceiro <strong>{lockedPartnerName}</strong>{' '}
                            {labels.lockedAlert}
                        </span>
                    </div>
                )}

                {/* Tabela de exames */}
                {exams.length > 0 && (
                    <div className="overflow-x-auto mb-4">
                        <table className="table table-sm">
                            <thead>
                                {/* O nome e o tipo do item NÃO são coluna: vão numa
                                    linha própria acima de cada item. Como coluna eles
                                    eram o campo mais largo da tabela e empurravam o
                                    conjunto para a rolagem horizontal. */}
                                <tr className="text-xs text-base-content/50">
                                    {/* "Fonte do valor" saiu daqui: escolher o parceiro
                                        duas vezes (uma para o preço, outra para o
                                        repasse) permitia que as duas se contradissessem.
                                        O preço agora deriva de quem recebe, e continua
                                        editável na coluna ao lado. */}
                                    <th className="w-56">Quem recebe</th>
                                    {showComponentSchedule && (
                                        <th className="w-52">
                                            {rooms.length > 0
                                                ? 'Quando e onde'
                                                : 'Quando'}
                                        </th>
                                    )}
                                    <th>Valor de venda</th>
                                    <th className="w-20">Qtd.</th>
                                    <th className="text-right">Subtotal</th>
                                    <th className="text-center">Cortesia</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {exams.map((exam, index) => {
                                    const hasPartners =
                                        exam.availablePartners.length > 0
                                    const hasProfessionals =
                                        exam.availableProfessionals.length > 0
                                    const rowVariations =
                                        variationsByCatalog[exam.catalogId] ||
                                        []
                                    const usedVariationIds = new Set(
                                        exams
                                            .filter(
                                                e =>
                                                    e.catalogId ===
                                                        exam.catalogId &&
                                                    e.catalogVariationId != null
                                            )
                                            .map(e => e.catalogVariationId)
                                    )
                                    const canAddVariation =
                                        usedVariationIds.size <
                                        rowVariations.length
                                    return (
                                        <Fragment key={index}>
                                            <tr
                                                className={
                                                    exam.complimentary
                                                        ? 'opacity-60'
                                                        : ''
                                                }
                                            >
                                                <td
                                                    colSpan={
                                                        showComponentSchedule
                                                            ? 7
                                                            : 6
                                                    }
                                                    className="border-b-0 pb-0 align-bottom"
                                                >
                                                    <span className="font-medium">
                                                        {exam.name}
                                                    </span>
                                                    {exam.catalogTypeName && (
                                                        <span className="text-xs text-base-content/50 ml-2">
                                                            {
                                                                exam.catalogTypeName
                                                            }
                                                        </span>
                                                    )}
                                                    {rowVariations.length >
                                                        0 && (
                                                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                                            {/* Combobox com busca (itens podem ter muitas
                                                            variações). Cada variação escolhida vira uma
                                                            linha própria via "Variação". */}
                                                            <SearchableSelect
                                                                className="w-full max-w-[220px]"
                                                                searchThreshold={
                                                                    1
                                                                }
                                                                value={
                                                                    exam.catalogVariationId ??
                                                                    null
                                                                }
                                                                emptyLabel="Sem variação (preço base)"
                                                                placeholder="Buscar variação..."
                                                                options={rowVariations.map(
                                                                    v => {
                                                                    const own =
                                                                        variationBasePrice(
                                                                            v,
                                                                            companyId
                                                                        )
                                                                    const from =
                                                                        own > 0
                                                                            ? null
                                                                            : variationPriceFrom(
                                                                                  v
                                                                              )
                                                                    return {
                                                                        value: v.id,
                                                                        label: v.name,
                                                                        // Zero não vira "R$ 0,00" e o mínimo dos
                                                                        // vínculos nunca vai sem "a partir de" —
                                                                        // ver `variationPriceFrom`.
                                                                        hint:
                                                                            own > 0
                                                                                ? formatCents(
                                                                                      own
                                                                                  )
                                                                                : from !=
                                                                                    null
                                                                                  ? `a partir de ${formatCents(from)}`
                                                                                  : undefined,
                                                                        disabled:
                                                                            usedVariationIds.has(
                                                                                v.id
                                                                            ) &&
                                                                            v.id !==
                                                                                exam.catalogVariationId,
                                                                    }
                                                                    }
                                                                )}
                                                                onChange={val =>
                                                                    handleVariationChange(
                                                                        index,
                                                                        val ==
                                                                            null
                                                                            ? null
                                                                            : Number(
                                                                                  val
                                                                              )
                                                                    )
                                                                }
                                                            />
                                                            {exam.catalogVariationId !=
                                                                null &&
                                                                canAddVariation && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-ghost btn-sm text-secondary gap-1"
                                                                        title="Adicionar outra variação deste item"
                                                                        onClick={() =>
                                                                            handleAddVariationRow(
                                                                                index
                                                                            )
                                                                        }
                                                                    >
                                                                        <MdAdd />{' '}
                                                                        Variação
                                                                    </button>
                                                                )}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                            <tr
                                                className={
                                                    exam.complimentary
                                                        ? 'opacity-60'
                                                        : ''
                                                }
                                            >
                                                {/* QUEM RECEBE.
                                                Existe porque quem executa o componente
                                                não é necessariamente quem executa o
                                                procedimento: a consulta pré-anestésica
                                                pode ser do hospital, de um parceiro ou de
                                                um profissional contratado. Sem isto o
                                                custo era cobrado e a conta a pagar nunca
                                                nascia. */}
                                                <td>
                                                    <select
                                                        className="select select-bordered select-sm w-full mb-1"
                                                        value={
                                                            exam.recipientKind ??
                                                            ''
                                                        }
                                                        onChange={ev =>
                                                            handleRecipientKindChange(
                                                                index,
                                                                ev.target.value
                                                            )
                                                        }
                                                    >
                                                        <option value="">
                                                            Selecione...
                                                        </option>
                                                        <option value="PARTNER">
                                                            Parceiro
                                                        </option>
                                                        {canSeeProfessionals && (
                                                            <option value="PROFESSIONAL">
                                                                Profissional
                                                            </option>
                                                        )}
                                                        <option value="HOSPITAL">
                                                            Hospital
                                                        </option>
                                                    </select>

                                                    {exam.recipientKind ===
                                                        'PARTNER' && (
                                                        <PartnerSelector
                                                            value={
                                                                exam.recipientId ??
                                                                null
                                                            }
                                                            label=""
                                                            size="sm"
                                                            companyId={
                                                                companyId
                                                            }
                                                            onChange={(
                                                                partnerId,
                                                                partner
                                                            ) =>
                                                                handleRecipientEntityChange(
                                                                    index,
                                                                    partnerId,
                                                                    partner?.name ??
                                                                        null
                                                                )
                                                            }
                                                        />
                                                    )}

                                                    {exam.recipientKind ===
                                                        'PROFESSIONAL' && (
                                                        <ProfessionalSearchSelector
                                                            value={
                                                                exam.recipientId ??
                                                                null
                                                            }
                                                            label=""
                                                            size="sm"
                                                            onChange={(
                                                                professionalId,
                                                                professional
                                                            ) =>
                                                                handleRecipientEntityChange(
                                                                    index,
                                                                    professionalId,
                                                                    professional?.name ??
                                                                        null
                                                                )
                                                            }
                                                        />
                                                    )}

                                                    {exam.recipientKind ===
                                                        'HOSPITAL' && (
                                                        <HospitalSearchSelector
                                                            value={
                                                                exam.recipientId ??
                                                                null
                                                            }
                                                            label=""
                                                            size="sm"
                                                            onChange={(
                                                                hospitalId,
                                                                hospital
                                                            ) =>
                                                                handleRecipientEntityChange(
                                                                    index,
                                                                    hospitalId,
                                                                    hospital?.name ??
                                                                        null
                                                                )
                                                            }
                                                        />
                                                    )}

                                                    {/* O repasse é quase sempre o valor
                                                    cheio: mostra em texto e só vira
                                                    campo pelo lápis. */}
                                                    {exam.recipientKind &&
                                                        (editingAmount.includes(
                                                            index
                                                        ) ? (
                                                            <div className="flex items-center gap-1 mt-1">
                                                                <span className="text-xs text-base-content/50 shrink-0">
                                                                    Repasse:
                                                                </span>
                                                                <input
                                                                    type="text"
                                                                    inputMode="numeric"
                                                                    autoFocus
                                                                    className="input input-bordered input-sm w-full"
                                                                    placeholder="Valor do repasse"
                                                                    value={currencyDisplay(
                                                                        exam.recipientAmount ??
                                                                            defaultRecipientAmount(
                                                                                exam
                                                                            )
                                                                    )}
                                                                    onChange={ev =>
                                                                        handleRecipientAmountChange(
                                                                            index,
                                                                            ev
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                    onBlur={() =>
                                                                        setEditingAmount(
                                                                            prev =>
                                                                                prev.filter(
                                                                                    i =>
                                                                                        i !==
                                                                                        index
                                                                                )
                                                                        )
                                                                    }
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-1 mt-1">
                                                                {/* Sem rótulo, o valor
                                                                aparecia solto embaixo do
                                                                destinatário e podia ser
                                                                lido como preço. */}
                                                                <span className="text-xs text-base-content/50">
                                                                    Repasse:
                                                                </span>
                                                                <span className="text-xs font-medium">
                                                                    {formatCents(
                                                                        exam.recipientAmount ??
                                                                            defaultRecipientAmount(
                                                                                exam
                                                                            )
                                                                    )}
                                                                </span>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-ghost btn-sm px-1"
                                                                    title="Editar o valor do repasse"
                                                                    onClick={() =>
                                                                        setEditingAmount(
                                                                            prev => [
                                                                                ...prev,
                                                                                index,
                                                                            ]
                                                                        )
                                                                    }
                                                                >
                                                                    <MdEdit />
                                                                </button>
                                                                {exam.recipientAmountEdited && (
                                                                    <button
                                                                        type="button"
                                                                        className="text-[10px] text-base-content/50 underline"
                                                                        onClick={() =>
                                                                            handleRecipientAmountReset(
                                                                                index
                                                                            )
                                                                        }
                                                                    >
                                                                        valor
                                                                        cheio
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ))}
                                                </td>
                                                {showComponentSchedule && (
                                                    <td>
                                                        <input
                                                            type={
                                                                exam.arrivalOrder
                                                                    ? 'date'
                                                                    : 'datetime-local'
                                                            }
                                                            className="input input-bordered input-sm w-full"
                                                            value={
                                                                exam.scheduledAt
                                                                    ? exam.scheduledAt.substring(
                                                                          0,
                                                                          exam.arrivalOrder
                                                                              ? 10
                                                                              : 16
                                                                      )
                                                                    : ''
                                                            }
                                                            onChange={ev =>
                                                                handleScheduleChange(
                                                                    index,
                                                                    ev.target
                                                                        .value
                                                                )
                                                            }
                                                        />
                                                        <label className="label cursor-pointer justify-start gap-1 py-0">
                                                            <input
                                                                type="checkbox"
                                                                className="checkbox checkbox-sm"
                                                                checked={
                                                                    !!exam.arrivalOrder
                                                                }
                                                                onChange={ev =>
                                                                    handleArrivalOrderChange(
                                                                        index,
                                                                        ev
                                                                            .target
                                                                            .checked
                                                                    )
                                                                }
                                                            />
                                                            <span className="label-text text-xs">
                                                                Ordem de chegada
                                                            </span>
                                                        </label>
                                                        {rooms.length > 0 && (
                                                            <select
                                                                className="select select-bordered select-sm w-full mt-1"
                                                                value={
                                                                    exam.roomId ??
                                                                    ''
                                                                }
                                                                onChange={ev =>
                                                                    handleRoomChange(
                                                                        index,
                                                                        ev.target
                                                                            .value
                                                                            ? parseInt(
                                                                                  ev
                                                                                      .target
                                                                                      .value
                                                                              )
                                                                            : null
                                                                    )
                                                                }
                                                            >
                                                                <option value="">
                                                                    Sem sala
                                                                </option>
                                                                {rooms.map(r => (
                                                                    <option
                                                                        key={
                                                                            r.id
                                                                        }
                                                                        value={
                                                                            r.id
                                                                        }
                                                                    >
                                                                        {r.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        )}
                                                    </td>
                                                )}
                                                <td
                                                    className={`${exam.complimentary ? 'line-through text-base-content/40' : ''}`}
                                                >
                                                    {/* Sempre editável: sem a "Fonte do
                                                    valor", digitar é o único jeito de
                                                    cobrar diferente do preço do item ou
                                                    do vínculo. Digitar marca a linha
                                                    como personalizada, e a partir daí
                                                    trocar o destinatário não sobrescreve
                                                    o que foi digitado. */}
                                                    {!exam.complimentary ? (
                                                        <input
                                                            type="text"
                                                            inputMode="numeric"
                                                            className="input input-bordered input-sm w-28"
                                                            placeholder="R$ 0,00"
                                                            value={currencyDisplay(
                                                                exam.unitPrice
                                                            )}
                                                            onChange={e =>
                                                                handleUnitPriceChange(
                                                                    index,
                                                                    parseCurrencyDigits(
                                                                        e.target
                                                                            .value
                                                                    )
                                                                )
                                                            }
                                                        />
                                                    ) : (
                                                        formatCents(
                                                            exam.unitPrice
                                                        )
                                                    )}
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        className="input input-bordered input-sm w-16"
                                                        value={exam.quantity}
                                                        onChange={e =>
                                                            handleQuantityChange(
                                                                index,
                                                                parseInt(
                                                                    e.target
                                                                        .value
                                                                ) || 1
                                                            )
                                                        }
                                                    />
                                                </td>
                                                <td
                                                    className={`text-right font-medium ${exam.complimentary ? 'line-through text-base-content/40' : 'text-secondary'}`}
                                                >
                                                    {formatCents(
                                                        exam.unitPrice *
                                                            exam.quantity
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    <input
                                                        type="checkbox"
                                                        className="toggle toggle-xs toggle-info"
                                                        checked={
                                                            exam.complimentary
                                                        }
                                                        onChange={() =>
                                                            handleToggleComplimentary(
                                                                index
                                                            )
                                                        }
                                                        title="Marcar como cortesia (sem custo)"
                                                    />
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="btn btn-ghost btn-sm text-error"
                                                        onClick={() =>
                                                            handleRemove(index)
                                                        }
                                                    >
                                                        <MdDelete size={14} />
                                                    </button>
                                                </td>
                                            </tr>
                                        </Fragment>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Busca para adicionar */}
                <div className="relative" ref={dropdownRef}>
                    <input
                        type="text"
                        className="input input-bordered input-sm w-full"
                        placeholder={
                            lockedPartnerId != null
                                ? labels.searchPlaceholderLocked(
                                      lockedPartnerName || ''
                                  )
                                : labels.searchPlaceholder
                        }
                        value={search}
                        onChange={e => {
                            searchExams(e.target.value)
                            setShowDropdown(true)
                        }}
                        onFocus={() => {
                            if (results.length > 0) setShowDropdown(true)
                        }}
                    />
                    {searching && (
                        <span className="loading loading-spinner loading-xs absolute z-10 right-3 top-2" />
                    )}
                    {showDropdown && results.length > 0 && (
                        <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {results.map(exam => {
                                const partnerOpt =
                                    lockedPartnerId != null
                                        ? (exam.partners || []).find(
                                              (p: any) =>
                                                  p.partnerId ===
                                                  lockedPartnerId
                                          )
                                        : null
                                const examBase = catalogBasePrice(
                                    exam,
                                    companyId
                                )
                                const displayPrice = partnerOpt
                                    ? partnerOpt.sellingPrice ||
                                      partnerOpt.price ||
                                      examBase
                                    : examBase
                                return (
                                    <button
                                        key={exam.id}
                                        type="button"
                                        className="w-full text-left px-3 py-2 hover:bg-base-200 flex items-center justify-between text-sm"
                                        onClick={() => handleAdd(exam)}
                                    >
                                        <div>
                                            <p className="font-medium">
                                                {exam.name}
                                            </p>
                                            {exam.catalogType && (
                                                <span className="text-xs text-base-content/50">
                                                    {exam.catalogType.name}
                                                </span>
                                            )}
                                        </div>
                                        <span className="text-xs font-semibold text-secondary">
                                            {formatCents(displayPrice)}
                                        </span>
                                    </button>
                                )
                            })}
                        </div>
                    )}
                    {showDropdown &&
                        !searching &&
                        search.length >= 2 &&
                        results.length === 0 && (
                            <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg p-3 text-xs text-base-content/60">
                                {lockedPartnerId != null
                                    ? labels.notFoundLocked
                                    : labels.notFound}
                            </div>
                        )}
                </div>

                {exams.length === 0 && (
                    <p className="text-sm text-base-content/40 mt-2">
                        {labels.addEmptyHint}
                    </p>
                )}
            </div>
        </div>
    )
}
